import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    type UpdateUserFormSchemaValues,
    updateUserFormSchema,
    useUpdateUser,
} from 'src/features/profile/api/useUpdateUser';
import { emailMessages } from 'src/features/profile/utils';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

import type { User } from 'src/features/auth';

type ProfileInfoFormProps = {
    userId: string;
    data: User;
    refetch: () => void;
};

const ProfileEmailForm = ({ data, userId, refetch }: ProfileInfoFormProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<UpdateUserFormSchemaValues>({
        resolver: zodResolver(updateUserFormSchema),
        mode: 'onChange',
        defaultValues: {
            email: data.email,
        },
    });

    const { mutate, isPending } = useUpdateUser({
        onSuccess: ({ email }) => {
            form.reset({ email });
            refetch();
            toast({
                title: formatMessage(emailMessages.emailChangeSuccess),
                description: formatMessage(emailMessages.emailChangeSuccessDetails, { email }),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    // See src/features/profile/components/ProfileSettingsForm.tsx for explanation
    React.useEffect(() => {
        void form.trigger();
    }, [form]);

    const onSubmit = (values: UpdateUserFormSchemaValues) => {
        mutate({ userId, ...values });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(emailMessages.email)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(emailMessages.emailSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" placeholder="user@smartpark.com" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    className="w-full sm:w-auto"
                    type="submit"
                    disabled={!(form.formState.isDirty && form.formState.isValid) || isPending}
                >
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default ProfileEmailForm;
